import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

/**
 * Bloque 5A — Traslado y entrega Partner Point.
 * admin=true  → contexto + "Marcar enviado" (OrderDetailsModal)
 * admin=false → estado + "Confirmar recepción" + entrega con PIN (Partner)
 * Se oculta solo si la orden no pertenece a un Partner Point.
 */
const Step = ({ done, label, date }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className={`w-2.5 h-2.5 rounded-full border ${done ? "bg-emerald-500 border-emerald-600" : "bg-white border-neutral-400"}`} />
    <span className={done ? "font-bold" : "text-[var(--text-secondary)]"}>{label}</span>
    {date && <span className="font-mono-tech text-[10px] text-[var(--text-secondary)]">{String(date).slice(0, 10)}</span>}
  </div>
);

export const PartnerDeliveryBlock = ({ orderId, admin = false }) => {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState("");

  const load = useCallback(() => {
    const url = admin
      ? `${API}/partner-orders/admin/${orderId}/partner-context`
      : `${API}/partner-orders/${orderId}/state`;
    axios
      .get(url, { withCredentials: true })
      .then(({ data }) => {
        if (admin && !data.is_partner_order) return setHidden(true);
        setState(admin ? { ...data.delivery, business_name: data.business_name } : data);
      })
      .catch(() => setHidden(true));
  }, [orderId, admin]);

  useEffect(() => { if (orderId) load(); }, [orderId, load]);

  if (!orderId || hidden || (admin && !state)) return null;
  const s = state || {};
  const delivered = !!s.delivered;

  const act = async (path, body = {}) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/partner-orders/${path}`, body, { withCredentials: true });
      toast.success(t("pd_ok"));
      setPin("");
      if (admin) load(); else setState((prev) => ({ ...prev, ...data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pd_error"));
      if (!admin) load();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="partner-delivery-block" className="p-4 border-b border-neutral-200 bg-amber-50/50 space-y-3">
      <div className="font-mono-tech text-[0.65rem] font-bold tracking-widest uppercase">
        {t("pd_title")}{s.business_name ? ` · ${s.business_name}` : ""}
      </div>
      <div className="flex flex-wrap gap-5">
        <Step done={!!s.sent_at} label={t("pd_step_sent")} date={s.sent_at} />
        <Step done={!!s.received_at} label={t("pd_step_received")} date={s.received_at} />
        <Step done={delivered} label={t("pd_step_delivered")} date={s.delivered_at} />
      </div>
      {admin && !delivered && (
        <button
          data-testid="pd-mark-sent-btn"
          disabled={busy}
          onClick={() => act(`admin/${orderId}/mark-sent`)}
          className="border border-black bg-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50"
        >
          {s.sent_at ? t("pd_resend_btn") : t("pd_mark_sent_btn")}
        </button>
      )}
      {admin && s.sent_at && !delivered && (
        <p className="text-[11px] text-[var(--text-secondary)]">{t("pd_pin_note_admin")}</p>
      )}
      {!admin && s.sent_at && !s.received_at && (
        <button
          data-testid="pd-confirm-received-btn"
          disabled={busy}
          onClick={() => act(`${orderId}/confirm-received`)}
          className="border border-black bg-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50"
        >
          {t("pd_confirm_received_btn")}
        </button>
      )}
      {!admin && s.received_at && !delivered && (
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="font-mono-tech text-[10px] uppercase block">{t("pd_pin_label")}</label>
            <input
              data-testid="pd-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="······"
              className="border border-black px-3 py-1.5 w-32 font-mono-tech text-lg tracking-[0.3em]"
            />
          </div>
          <button
            data-testid="pd-deliver-btn"
            disabled={busy || pin.length < 4 || s.pin_locked}
            onClick={() => act(`${orderId}/deliver`, { pin })}
            className="border border-emerald-600 bg-emerald-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50"
          >
            {t("pd_deliver_btn")}
          </button>
          {s.pin_locked ? (
            <span className="text-xs text-red-600 font-bold">{t("pd_pin_locked")}</span>
          ) : (
            <span className="text-[11px] text-[var(--text-secondary)]">
              {t("pd_attempts_left")} {s.attempts_left}
            </span>
          )}
        </div>
      )}
      {delivered && (
        <div data-testid="pd-delivered-badge" className="text-xs font-bold text-emerald-700">
          {t("pd_delivered_msg")}
        </div>
      )}
    </div>
  );
};

export default PartnerDeliveryBlock;
